import { PermissionEnum } from '@Application/Security/Roles';
import FetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Queries/FetchBeneficiaryContacts';
import fetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/fetchBeneficiaryContacts';
import FetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/Queries/FetchStaffMembers';
import fetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/fetchStaffMembers';
import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import mergeInterviewEntries from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/mergeInterviewEntries';
import type { InterviewParticipantLabels } from '@UI/Pages/Beneficiaries/Hooks/Helpers/resolveServerParticipants';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useLocalInterviews from '@UI/Pages/Beneficiaries/Hooks/useLocalInterviews';
import useSubjectInterviews from '@UI/Pages/Beneficiaries/Hooks/useSubjectInterviews';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Result = {
	/** Fused, sorted, never truncated (`mergeInterviewEntries`'s own contract) — truncation is the
	 * caller's decision. */
	readonly entries: readonly InternalInterviewListEntryView[];
	/** Mirrors `useStoreQuery`'s own `data !== null`: the caller must keep using it, never `isLoading`,
	 * to decide the first-load skeleton (`guides/state-stores.md`, "Ce que `useStoreQuery` ne garantit
	 * pas"). */
	readonly isServerDataLoaded: boolean;
	/** `LocalInterviewPort.isHydrated()` — false until the device's own store has been read once.
	 * A dossier holding only local interviews must never read as empty during that window. */
	readonly isLocalStoreHydrated: boolean;
	readonly error: Error | null;
	readonly refetch: () => void;
	/** The server-side list as loaded, before any fusion: the detail screen resolves its own subject
	 * from it, rather than duplicating this hook's three queries. */
	readonly serverInterviews: readonly InterviewView[];
	/** This dossier's local interviews, already filtered by the dossier address. */
	readonly localInterviews: readonly LocalInterview[];
	/** The two referentials, resolved once here, so a consumer never rebuilds them. */
	readonly participantLabels: InterviewParticipantLabels;
};

/**
 * Composes the two sources an interview can live in — the server list and this device's own local
 * interviews — plus the transfer queue, into the rows the dossier's interview section renders.
 *
 * The server list comes from `useSubjectInterviews`. `RECORDING_COMPLETED` invalidates it (the server
 * creates a processing the response does not carry), so it is read again; `INTERVIEW_CREATED` and
 * `RECORDING_SEGMENT_TRANSFERRED` update `interviewsStore` directly from their reducer.
 *
 * `refetch` is exposed to cover the one case the store cannot: a transfer confirmed from another
 * device, which never runs this browser's reducer.
 */
function useBeneficiaryInterviewsList(subject: SubjectAddress): Result {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { isBeneficiary, subjectId } = subject;
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const hasStaffReadPermission = useHasPermission(PermissionEnum.STAFF_READ);

	const localInterviewPort = useLocalInterviewPort();
	const allLocalInterviews = useLocalInterviews();
	const outboxItems = useOutboxItems();

	const { data: interviews, error, refetch } = useSubjectInterviews(subject);

	// Neither referential gates or delays the interview list itself (rule 5 of the plan's angles
	// morts): a failed or still-loading fetch here never enters `isServerDataLoaded`, it only leaves
	// the fallback labels (`Contact n°{id}` / `Membre du personnel n°{id}`) in place.
	const { data: contacts } = useStoreQuery({
		selector: (state) => state.beneficiaryContactsStore.contacts,
		stopOn: (value) => !isBeneficiary || (value !== null && value.beneficiaryId === subjectId),
		query: () => {
			if (!isBeneficiary) {
				return Promise.resolve();
			}

			return dispatch(fetchBeneficiaryContacts(new FetchBeneficiaryContacts(facilityId, subjectId)));
		},
		deps: [facilityId, subjectId, isBeneficiary],
	});

	// Guarded on `STAFF_READ`: a custom role without it would otherwise draw a 403 from
	// `GET /api/organizations/{organization}/staff-members` on every dossier it opens. Guarded on
	// `organizationId` resolving, exactly like `StaffAssignmentPanel`: `Promise.resolve()` is the
	// established way to make `useStoreQuery` skip a request rather than dispatch with a null id.
	const { data: staffMembers } = useStoreQuery({
		selector: (state) => state.staffMembersStore.staffMembers,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null || !hasStaffReadPermission) {
				return Promise.resolve();
			}

			return dispatch(fetchStaffMembers(new FetchStaffMembers(organizationId)));
		},
		deps: [organizationId, hasStaffReadPermission],
	});

	const participantLabels = React.useMemo<InterviewParticipantLabels>(
		() => ({
			contactNameById: new Map((contacts?.list ?? []).map((contact) => [contact.id, contact.fullName])),
			staffMemberNameById: new Map(
				(staffMembers?.list ?? []).map((staffMember) => [staffMember.id, staffMember.fullName]),
			),
		}),
		[contacts, staffMembers],
	);

	const localInterviews = React.useMemo(
		() =>
			allLocalInterviews.filter(
				(localInterview) =>
					localInterview.subject.typeKey === subject.typeKey &&
					localInterview.subject.subjectId === subjectId,
			),
		[allLocalInterviews, subject.typeKey, subjectId],
	);

	const entries = React.useMemo(
		() => mergeInterviewEntries(interviews?.list ?? [], localInterviews, outboxItems, participantLabels),
		[interviews, localInterviews, outboxItems, participantLabels],
	);

	// The detail screen resolves its own subject from the raw server list: memoized separately so a
	// consumer's own `useMemo` never sees a new array reference on a render this list did not change.
	const serverInterviews = React.useMemo(() => interviews?.list ?? [], [interviews]);

	return {
		entries,
		isServerDataLoaded: interviews !== null,
		// Read outside `useSyncExternalStore`, so this boolean carries no re-render trigger of its own.
		// It is only re-read at the right moment because `useLocalInterviews`, called above, subscribes
		// to the same port and re-renders this component on every notification — including the one
		// `hydrate()` fires when it flips `isHydrated()` to `true` (`LocalInterviewPort.isHydrated()`).
		isLocalStoreHydrated: localInterviewPort.isHydrated(),
		error,
		refetch,
		serverInterviews,
		localInterviews,
		participantLabels,
	};
}

export default useBeneficiaryInterviewsList;
